import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  ChevronDown, Menu, X, ArrowRight, Mail, MessageCircle, Home as HomeIcon, DraftingCompass, Globe2,
  Factory, Workflow, BadgeDollarSign, Building2, Newspaper, LayoutGrid, MonitorPlay, Map as MapIcon,
  Landmark, Calculator, RefreshCcw, ShieldCheck, HardHat, Zap, Sun, TrainFront, Mountain, Tractor,
  TreePine, Radio, FileText,
} from "lucide-react";
import Logo from "./Logo";
import { SERVICES } from "../data/services";
import { FOCUSED_PAGES } from "../data/focusedPages";
import { INTL_COUNTRIES } from "../data/intl";
import { POSTS } from "../data/posts";
import { PROCESS_STEPS, SITE } from "../lib/site";
import { FLAGS } from "./Flags";

const linkCls = (active) =>
  `inline-flex items-center gap-1 whitespace-nowrap text-[13px] font-medium transition-colors ${active ? "text-aqua" : "text-ink hover:text-steel"}`;

// Single dropdown item with icon + focusing detail
function MenuItem({ icon: Icon, title, desc, to, href, testid, onNavigate }) {
  const inner = (
    <>
      {Icon && <Icon className="h-4 w-4 mt-0.5 text-aqua shrink-0" />}
      <span>
        <span className="block text-sm font-medium text-ink group-hover/item:text-steel leading-snug">{title}</span>
        {desc && <span className="mt-0.5 block text-xs text-mutedslate leading-snug">{desc}</span>}
      </span>
    </>
  );
  const cls = "flex items-start gap-3 px-4 py-2.5 rounded-sm hover:bg-paleslate transition-colors group/item";
  if (href) {
    return (
      <a href={href} target={href.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer" data-testid={testid} onClick={onNavigate} className={cls}>
        {inner}
      </a>
    );
  }
  return (
    <Link to={to} onClick={onNavigate} data-testid={testid} className={cls}>
      {inner}
    </Link>
  );
}

// Desktop nav item: icon + label link + chevron trigger + dropdown panel
function DesktopDropdown({ to, label, icon: Icon, active, linkTestId, triggerTestId, panelTestId, width = "w-[560px]", children }) {
  const [ddOpen, setDdOpen] = useState(false);
  return (
    <div className="relative" onMouseEnter={() => setDdOpen(true)} onMouseLeave={() => setDdOpen(false)}>
      <div className="flex items-center gap-0.5">
        <Link to={to} data-testid={linkTestId} className={linkCls(active)}>
          <Icon className="h-3.5 w-3.5" />
          {label}
        </Link>
        <button
          data-testid={triggerTestId}
          className={`p-1 transition-colors ${active || ddOpen ? "text-aqua" : "text-ink hover:text-steel"}`}
          aria-haspopup="true"
          aria-expanded={ddOpen}
          aria-label={`Open ${label} menu`}
          onClick={() => setDdOpen(!ddOpen)}
        >
          <ChevronDown className={`h-3.5 w-3.5 transition-transform ${ddOpen ? "rotate-180" : ""}`} />
        </button>
      </div>
      <div className={`${ddOpen ? "block" : "hidden"} lg:absolute lg:left-1/2 lg:-translate-x-1/2 lg:top-full lg:pt-4`}>
        <div
          className={`bg-white border border-lineblue rounded-md shadow-[0_16px_40px_rgba(16,42,67,0.12)] ${width} p-2 max-h-[72vh] overflow-y-auto`}
          data-testid={panelTestId}
        >
          {children(() => setDdOpen(false))}
        </div>
      </div>
    </div>
  );
}

const MARKET_DESC = {
  usa: "ALTA/NSPS · county formats · USD quotes",
  canada: "Legal plans · bilingual EN/FR · CAD quotes",
  uk: "Topographical surveys · Land Registry · GBP",
  australia: "Feature & level · GDA2020 · AUD quotes",
};

const INDUSTRY_ITEMS = [
  [HomeIcon, "Residential Development", "Lot surveys, plats, and mortgage locations"],
  [Building2, "Commercial Real Estate & Title", "ALTA/NSPS sheets and title support drawings"],
  [DraftingCompass, "Land Surveying Firms", "Overflow production for every survey type"],
  [HardHat, "Construction & Infrastructure", "Staking sheets, as-builts, progress documentation"],
  [Zap, "Utilities & Energy", "Utility mapping, corridors, as-built records"],
  [Sun, "Renewable Energy", "Solar and wind site base mapping"],
  [TrainFront, "Transportation & Public Works", "Route, ROW, and corridor drawing sets"],
  [Landmark, "Municipal & Government", "Parcel mapping and public works documentation"],
  [Mountain, "Mining & Aggregates", "Pit, stockpile, and progress drawings"],
  [Tractor, "Agriculture & Land Management", "Parcel plans and land-base mapping"],
  [TreePine, "Environmental & Conservation", "Feature and terrain mapping deliverables"],
  [Radio, "Telecommunications", "Site and route documentation for networks"],
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [mobileSection, setMobileSection] = useState(null);
  const location = useLocation();
  const p = location.pathname;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setMobileSection(null);
  }, [p]);

  const isServicePage = p === "/services" || SERVICES.some((s) => `/${s.slug}` === p) || FOCUSED_PAGES.some((f) => `/${f.slug}` === p);
  const isMarketPage = INTL_COUNTRIES.some((c) => p === `/${c.slug}` || p.startsWith(`/${c.slug}/`));

  // Mobile collapsible section helper
  const MobileGroup = ({ id, icon: Icon, label, to, linkTestId, toggleTestId, children }) => (
    <div>
      <div className="flex items-center rounded-md hover:bg-paleslate">
        <Link to={to} data-testid={linkTestId} className="flex-1 inline-flex items-center gap-2.5 px-3 py-2.5 text-base font-medium text-ink">
          <Icon className="h-4 w-4 text-aqua" />
          {label}
        </Link>
        <button
          data-testid={toggleTestId}
          className="px-3 py-2.5 text-ink"
          onClick={() => setMobileSection(mobileSection === id ? null : id)}
          aria-expanded={mobileSection === id}
          aria-label={`Toggle ${label} list`}
        >
          <ChevronDown className={`h-4 w-4 transition-transform ${mobileSection === id ? "rotate-180" : ""}`} />
        </button>
      </div>
      {mobileSection === id && <div className="pl-4 pb-2 space-y-0.5">{children}</div>}
    </div>
  );

  const MobileSub = ({ to, href, icon: Icon, title, desc, testid }) => (
    href ? (
      <a href={href} target={href.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer" data-testid={testid} className="flex items-start gap-2.5 px-3 py-2 text-sm text-mutedslate rounded-md hover:bg-paleslate hover:text-steel">
        {Icon && <Icon className="h-3.5 w-3.5 mt-0.5 text-aqua shrink-0" />}
        <span>{title}{desc && <span className="block text-xs text-mutedslate/70">{desc}</span>}</span>
      </a>
    ) : (
      <Link to={to} data-testid={testid} className="flex items-start gap-2.5 px-3 py-2 text-sm text-mutedslate rounded-md hover:bg-paleslate hover:text-steel">
        {Icon && <Icon className="h-3.5 w-3.5 mt-0.5 text-aqua shrink-0" />}
        <span>{title}{desc && <span className="block text-xs text-mutedslate/70">{desc}</span>}</span>
      </Link>
    )
  );

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-white/90 backdrop-blur-md border-b border-lineblue shadow-[0_4px_24px_rgba(16,42,67,0.06)]" : "bg-white/80 backdrop-blur-sm border-b border-transparent"
      }`}
    >
      <div className="bg-navy-deep border-b border-navy-light" data-testid="topbar">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-9 flex items-center justify-between">
          <p className="hidden sm:block font-mono text-[10px] uppercase tracking-[0.25em] text-white/50">
            Land Surveying CAD Drafting · GIS Production
          </p>
          <div className="flex items-center gap-5">
            <a
              href={SITE.whatsappLink}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="topbar-whatsapp"
              className="inline-flex items-center gap-2 font-mono text-[11px] tracking-wider text-aqua hover:text-white transition-colors"
            >
              <MessageCircle className="h-3.5 w-3.5" />
              <span className="hidden xs:inline sm:inline">WhatsApp </span>{SITE.whatsappNumber}
            </a>
            <a
              href={`mailto:${SITE.email}`}
              data-testid="topbar-email"
              className="hidden md:inline-flex items-center gap-2 font-mono text-[11px] tracking-wider text-aqua hover:text-white transition-colors"
            >
              <Mail className="h-3.5 w-3.5" />
              {SITE.email}
            </a>
          </div>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-[72px]">
          <Logo />

          <nav className="hidden lg:flex flex-1 items-center justify-evenly px-2" aria-label="Primary">
            {/* Home */}
            <DesktopDropdown to="/" label="Home" icon={HomeIcon} active={p === "/"} linkTestId="nav-link-home" triggerTestId="nav-trigger-home" panelTestId="nav-home-menu" width="w-[380px]">
              {(close) => (
                <>
                  <MenuItem icon={LayoutGrid} title="Homepage Overview" desc="CAD drafting & GIS production for surveyors" to="/" testid="nav-dd-home-overview" onNavigate={close} />
                  <MenuItem icon={MonitorPlay} title="Interactive CAD Showcase" desc="Watch a survey sheet build layer by layer" to="/" testid="nav-dd-home-showcase" onNavigate={close} />
                  <MenuItem icon={MapIcon} title="Coverage Map" desc="All 50 states, DC and 3,100+ county pages" to="/service-areas" testid="nav-dd-home-coverage" onNavigate={close} />
                  <MenuItem icon={RefreshCcw} title="Why Outsource Drafting" desc="Production capacity without hiring overhead" to="/survey-cad-outsourcing" testid="nav-dd-home-outsourcing" onNavigate={close} />
                </>
              )}
            </DesktopDropdown>

            {/* Services */}
            <DesktopDropdown to="/services" label="Services" icon={DraftingCompass} active={isServicePage} linkTestId="nav-link-services" triggerTestId="nav-trigger-services" panelTestId="nav-services-menu" width="w-[720px]">
              {(close) => (
                <>
                  <p className="px-4 pt-2 pb-1 font-mono text-[10px] uppercase tracking-[0.2em] text-mutedslate">High-intent programs</p>
                  <div className="grid grid-cols-2">
                    {FOCUSED_PAGES.map((f) => (
                      <MenuItem key={f.slug} icon={f.icon} title={f.keyword} desc={f.heroLead.split(".")[0] + "."} to={`/${f.slug}`} testid={`nav-focused-${f.slug}`} onNavigate={close} />
                    ))}
                  </div>
                  <p className="px-4 pt-3 pb-1 font-mono text-[10px] uppercase tracking-[0.2em] text-mutedslate border-t border-lineblue mt-2">All 22 production services</p>
                  <div className="grid grid-cols-2">
                    {SERVICES.map((s) => (
                      <Link
                        key={s.slug}
                        to={`/${s.slug}`}
                        onClick={close}
                        data-testid={`nav-service-${s.slug}`}
                        className="flex items-start gap-3 px-4 py-2.5 rounded-sm hover:bg-paleslate transition-colors group/item"
                      >
                        <s.icon className="h-4 w-4 mt-0.5 text-aqua shrink-0" />
                        <span>
                          <span className="block text-sm font-medium text-ink group-hover/item:text-steel leading-snug">{s.navLabel}</span>
                          <span className="mt-0.5 block text-xs text-mutedslate leading-snug line-clamp-2">{s.short}</span>
                        </span>
                      </Link>
                    ))}
                  </div>
                </>
              )}
            </DesktopDropdown>

            {/* Markets */}
            <DesktopDropdown to="/usa" label="Markets" icon={Globe2} active={isMarketPage} linkTestId="nav-link-markets" triggerTestId="nav-trigger-markets" panelTestId="nav-markets-menu" width="w-[640px]">
              {(close) => (
                <>
                  <div className="grid grid-cols-2">
                    {INTL_COUNTRIES.map((c) => (
                      <div key={c.slug} className="px-2 py-2">
                        <Link to={`/${c.slug}`} onClick={close} data-testid={`nav-market-${c.slug}`} className="flex items-center gap-2 px-2 py-1.5 rounded-sm hover:bg-paleslate transition-colors">
                          {FLAGS[c.slug]}
                          <span>
                            <span className="block text-sm font-semibold text-navy">{c.name}</span>
                            <span className="block text-[11px] text-mutedslate">{MARKET_DESC[c.slug]}</span>
                          </span>
                        </Link>
                        <div className="mt-1 flex flex-wrap gap-x-1 gap-y-0.5 pl-9">
                          {c.cities.slice(0, 4).map((ct) => (
                            <Link
                              key={ct.slug}
                              to={`/${c.slug}/city/${ct.slug}`}
                              onClick={close}
                              data-testid={`nav-market-city-${ct.slug}`}
                              className="text-xs text-mutedslate hover:text-aqua transition-colors after:content-['·'] after:ml-1 last:after:content-['']"
                            >
                              {ct.name}
                            </Link>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                  <Link
                    to="/service-areas"
                    onClick={close}
                    data-testid="nav-market-service-areas"
                    className="mt-1 flex items-center justify-between border-t border-lineblue px-4 py-3 text-xs font-semibold text-steel hover:text-aqua transition-colors"
                  >
                    Service Areas — all 50 US states + 3,100 county pages
                    <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                </>
              )}
            </DesktopDropdown>

            {/* Industries */}
            <DesktopDropdown to="/industries" label="Industries" icon={Factory} active={p === "/industries"} linkTestId="nav-link-industries" triggerTestId="nav-trigger-industries" panelTestId="nav-industries-menu" width="w-[640px]">
              {(close) => (
                <div className="grid grid-cols-2">
                  {INDUSTRY_ITEMS.map(([Icon, title, desc]) => (
                    <MenuItem key={title} icon={Icon} title={title} desc={desc} to="/industries" testid={`nav-industry-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/-$/, "")}`} onNavigate={close} />
                  ))}
                </div>
              )}
            </DesktopDropdown>

            {/* Our Process */}
            <DesktopDropdown to="/our-process" label="Our Process" icon={Workflow} active={p === "/our-process"} linkTestId="nav-link-process" triggerTestId="nav-trigger-process" panelTestId="nav-process-menu" width="w-[420px]">
              {(close) => (
                <>
                  {PROCESS_STEPS.map((s) => (
                    <MenuItem key={s.num} icon={FileText} title={`${s.num} — ${s.title}`} desc={s.desc} to="/our-process" testid={`nav-process-step-${s.num}`} onNavigate={close} />
                  ))}
                </>
              )}
            </DesktopDropdown>

            {/* Pricing */}
            <DesktopDropdown to="/pricing" label="Pricing" icon={BadgeDollarSign} active={p === "/pricing"} linkTestId="nav-link-pricing" triggerTestId="nav-trigger-pricing" panelTestId="nav-pricing-menu" width="w-[420px]">
              {(close) => (
                <>
                  <MenuItem icon={BadgeDollarSign} title="Pricing Models" desc="Per-sheet, per-project and retainer options" to="/pricing" testid="nav-dd-pricing-models" onNavigate={close} />
                  <MenuItem icon={Calculator} title="Quote Calculator" desc="Scope your project in three quick steps" to="/request-a-quote" testid="nav-dd-pricing-calculator" onNavigate={close} />
                  <MenuItem icon={RefreshCcw} title="Volume & Retainers" desc="Steady weekly production arrangements" to="/pricing" testid="nav-dd-pricing-volume" onNavigate={close} />
                </>
              )}
            </DesktopDropdown>

            {/* About Us */}
            <DesktopDropdown to="/about" label="About Us" icon={Building2} active={p === "/about"} linkTestId="nav-link-about" triggerTestId="nav-trigger-about" panelTestId="nav-about-menu" width="w-[420px]">
              {(close) => (
                <>
                  <MenuItem icon={Building2} title="Our Story" desc="A production partner built for surveyors" to="/about" testid="nav-dd-about-story" onNavigate={close} />
                  <MenuItem icon={MonitorPlay} title="Standards & Software" desc="AutoCAD, Civil 3D, Carlson, MicroStation, ArcGIS" to="/about" testid="nav-dd-about-standards" onNavigate={close} />
                  <MenuItem icon={ShieldCheck} title="Confidential Handling" desc="Project data under agreed confidentiality" to="/about" testid="nav-dd-about-confidentiality" onNavigate={close} />
                </>
              )}
            </DesktopDropdown>

            {/* Blog */}
            <DesktopDropdown to="/blog" label="Blog" icon={Newspaper} active={p.startsWith("/blog")} linkTestId="nav-link-blog" triggerTestId="nav-trigger-blog" panelTestId="nav-blog-menu" width="w-[460px]">
              {(close) => (
                <>
                  {POSTS.slice(0, 4).map((post) => (
                    <MenuItem key={post.slug} icon={Newspaper} title={post.title} desc={`${post.tag} · ${post.readMinutes} min read`} to={`/blog/${post.slug}`} testid={`nav-blog-post-${post.slug}`} onNavigate={close} />
                  ))}
                  <Link to="/blog" onClick={close} data-testid="nav-blog-all" className="mt-1 flex items-center justify-between border-t border-lineblue px-4 py-3 text-xs font-semibold text-steel hover:text-aqua transition-colors">
                    All drafting-knowledge articles
                    <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                </>
              )}
            </DesktopDropdown>

            {/* Contact */}
            <DesktopDropdown to="/contact" label="Contact" icon={Mail} active={p === "/contact"} linkTestId="nav-link-contact" triggerTestId="nav-trigger-contact" panelTestId="nav-contact-menu" width="w-[400px]">
              {(close) => (
                <>
                  <MenuItem icon={Mail} title="Email Us" desc={SITE.email} href={`mailto:${SITE.email}`} testid="nav-dd-contact-email" onNavigate={close} />
                  <MenuItem icon={MessageCircle} title="WhatsApp" desc={`${SITE.whatsappNumber} — quick chat, fast reply`} href={SITE.whatsappLink} testid="nav-dd-contact-whatsapp" onNavigate={close} />
                  <MenuItem icon={Calculator} title="Request a Quote" desc="Written scope, schedule and price" to="/request-a-quote" testid="nav-dd-contact-quote" onNavigate={close} />
                  <MenuItem icon={ShieldCheck} title="Customer Support Form" desc="Project support, revisions, file issues" to="/contact" testid="nav-dd-contact-support" onNavigate={close} />
                </>
              )}
            </DesktopDropdown>

          </nav>

          <Link
            to="/request-a-quote"
            data-testid="nav-cta-quote"
            className="hidden lg:inline-flex ml-2 items-center gap-1.5 whitespace-nowrap bg-aqua text-navy font-bold text-[13px] px-3.5 py-2 rounded-md hover:bg-aqua-deep hover:shadow-[0_8px_20px_rgba(32,196,199,0.35)] transition-all"
          >
            Request a Quote
            <ArrowRight className="h-4 w-4" />
          </Link>

          <button
            className="lg:hidden p-2 text-navy rounded-md hover:bg-paleslate transition-colors"
            onClick={() => setOpen(!open)}
            data-testid="mobile-menu-toggle"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className="lg:hidden bg-white border-b border-lineblue shadow-lg overflow-hidden"
            data-testid="mobile-menu"
          >
            <nav className="px-4 py-6 space-y-1 max-h-[75vh] overflow-y-auto" aria-label="Mobile">
              <MobileGroup id="home" icon={HomeIcon} label="Home" to="/" linkTestId="mobile-nav-home" toggleTestId="mobile-nav-home-toggle">
                <MobileSub to="/" icon={LayoutGrid} title="Homepage Overview" testid="mobile-dd-home-overview" />
                <MobileSub to="/service-areas" icon={MapIcon} title="Coverage Map" desc="50 states + county pages" testid="mobile-dd-home-coverage" />
                <MobileSub to="/survey-cad-outsourcing" icon={RefreshCcw} title="Why Outsource Drafting" testid="mobile-dd-home-outsourcing" />
              </MobileGroup>

              <MobileGroup id="services" icon={DraftingCompass} label="Services" to="/services" linkTestId="mobile-nav-services" toggleTestId="mobile-nav-services-toggle">
                {FOCUSED_PAGES.map((f) => (
                  <MobileSub key={f.slug} to={`/${f.slug}`} icon={f.icon} title={f.keyword} testid={`mobile-nav-focused-${f.slug}`} />
                ))}
                {SERVICES.map((s) => (
                  <MobileSub key={s.slug} to={`/${s.slug}`} icon={s.icon} title={s.navLabel} testid={`mobile-nav-service-${s.slug}`} />
                ))}
              </MobileGroup>

              <MobileGroup id="markets" icon={Globe2} label="Markets" to="/usa" linkTestId="mobile-nav-markets" toggleTestId="mobile-nav-markets-toggle">
                {INTL_COUNTRIES.map((c) => (
                  <MobileSub key={c.slug} to={`/${c.slug}`} title={c.name} desc={MARKET_DESC[c.slug]} testid={`mobile-nav-market-${c.slug}`} />
                ))}
                <MobileSub to="/service-areas" title="Service Areas" desc="All 50 US states + counties" testid="mobile-nav-market-service-areas" />
              </MobileGroup>

              <MobileGroup id="industries" icon={Factory} label="Industries" to="/industries" linkTestId="mobile-nav-industries" toggleTestId="mobile-nav-industries-toggle">
                {INDUSTRY_ITEMS.map(([Icon, title]) => (
                  <MobileSub key={title} to="/industries" icon={Icon} title={title} testid={`mobile-nav-industry-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/-$/, "")}`} />
                ))}
              </MobileGroup>

              <MobileGroup id="process" icon={Workflow} label="Our Process" to="/our-process" linkTestId="mobile-nav-process" toggleTestId="mobile-nav-process-toggle">
                {PROCESS_STEPS.map((s) => (
                  <MobileSub key={s.num} to="/our-process" icon={FileText} title={`${s.num} — ${s.title}`} testid={`mobile-nav-process-step-${s.num}`} />
                ))}
              </MobileGroup>

              <MobileGroup id="pricing" icon={BadgeDollarSign} label="Pricing" to="/pricing" linkTestId="mobile-nav-pricing" toggleTestId="mobile-nav-pricing-toggle">
                <MobileSub to="/pricing" icon={BadgeDollarSign} title="Pricing Models" testid="mobile-dd-pricing-models" />
                <MobileSub to="/request-a-quote" icon={Calculator} title="Quote Calculator" testid="mobile-dd-pricing-calculator" />
                <MobileSub to="/pricing" icon={RefreshCcw} title="Volume & Retainers" testid="mobile-dd-pricing-volume" />
              </MobileGroup>

              <MobileGroup id="about" icon={Building2} label="About Us" to="/about" linkTestId="mobile-nav-about" toggleTestId="mobile-nav-about-toggle">
                <MobileSub to="/about" icon={Building2} title="Our Story" testid="mobile-dd-about-story" />
                <MobileSub to="/about" icon={MonitorPlay} title="Standards & Software" testid="mobile-dd-about-standards" />
                <MobileSub to="/about" icon={ShieldCheck} title="Confidential Handling" testid="mobile-dd-about-confidentiality" />
              </MobileGroup>

              <MobileGroup id="blog" icon={Newspaper} label="Blog" to="/blog" linkTestId="mobile-nav-blog" toggleTestId="mobile-nav-blog-toggle">
                {POSTS.slice(0, 4).map((post) => (
                  <MobileSub key={post.slug} to={`/blog/${post.slug}`} icon={Newspaper} title={post.title} testid={`mobile-nav-blog-post-${post.slug}`} />
                ))}
                <MobileSub to="/blog" icon={Newspaper} title="All Articles" testid="mobile-nav-blog-all" />
              </MobileGroup>

              <MobileGroup id="contact" icon={Mail} label="Contact" to="/contact" linkTestId="mobile-nav-contact" toggleTestId="mobile-nav-contact-toggle">
                <MobileSub href={`mailto:${SITE.email}`} icon={Mail} title="Email Us" desc={SITE.email} testid="mobile-dd-contact-email" />
                <MobileSub href={SITE.whatsappLink} icon={MessageCircle} title="WhatsApp" desc={SITE.whatsappNumber} testid="mobile-dd-contact-whatsapp" />
                <MobileSub to="/request-a-quote" icon={Calculator} title="Request a Quote" testid="mobile-dd-contact-quote" />
                <MobileSub to="/contact" icon={ShieldCheck} title="Customer Support Form" testid="mobile-dd-contact-support" />
              </MobileGroup>

              <Link
                to="/request-a-quote"
                data-testid="mobile-nav-cta-quote"
                className="mt-3 flex items-center justify-center gap-2 bg-aqua text-navy font-bold px-5 py-3 rounded-md"
              >
                Request a Quote
                <ArrowRight className="h-4 w-4" />
              </Link>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
